import * as React from 'react'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'

import { ParticpantsContext } from 'containers/Participants'
import BulkTagsInput from 'components/Participants/BulkTag/BulkTagInput'
import { ParticipantTagInput } from 'types/Participants'
import BulkTagsAPI from 'api/Participant/BulkTag/Action'
import { useAlert } from 'components/Shared/Alert'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { fetchParticipantTags } from 'redux/thunks/ParticipantTags'
import {
  enableOrDisableBulkTag,
  getAlertTitle,
  getBulkTagRequestPayload,
} from 'components/Participants/BulkTag/Helper'

const BulkTag = () => {
  const [isOpen, onOpen] = React.useState(false)
  const [hasError, onError] = React.useState(false)
  const [loading, onLoading] = React.useState(false)
  const [tags, setTags] = React.useState<ParticipantTagInput[]>([])
  const { selectedParticipantIDs, selectAll } =
    React.useContext(ParticpantsContext).state
  const { setSelectAllObj, onSelection } =
    React.useContext(ParticpantsContext).functions
  const isBulkTagDisabled = enableOrDisableBulkTag(
    selectAll,
    selectedParticipantIDs,
  )
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const globalDispatch = useAppDispatch()
  const { segmentId } = useAppSelector((state) => state.filterBuilder)
  const resetState = () => {
    setTags([])
    onError(false)
    onLoading(false)
  }

  const handleClick = () => {
    onOpen(!isOpen)
    resetState()
  }

  const handleChange = (tags: ParticipantTagInput[]) => {
    if (hasError) onError(false)

    const formattedTags = tags.map((tag) => {
      if (tag.inputValue) {
        /**
         * Means it's is a new tag.
         */
        return {
          ...tag,
          name: tag.inputValue,
        }
      }

      return tag
    })

    setTags(formattedTags)
  }

  const handleErrorAlert = () => {
    showAlert({
      title: t('participant.bulkTag.alertError'),
    })

    onLoading(false)
  }

  const handleSuccessAlert = () => {
    const alertTitle = getAlertTitle(tags)

    showAlert({
      type: 'success',
      title: alertTitle,
    })
  }

  const hasEmptyTags = () => {
    if (!tags.length) {
      onError(true)

      return true
    }
  }

  const addTagsHandler = async () => {
    if (hasEmptyTags()) return

    onLoading(true)

    const request = getBulkTagRequestPayload({
      tags,
      selectedParticipantIDs,
      selectAll,
      segmentId,
    })

    const { addBulkTags } = BulkTagsAPI

    const response = await addBulkTags(request)

    if (response.code) {
      handleErrorAlert()

      return
    }
    setSelectAllObj({
      allVisible: false,
      all: false,
      page: 0,
    })
    onSelection([])
    handleSuccessAlert()
    await globalDispatch(fetchParticipantTags())
    handleClick()
  }

  return (
    <React.Fragment>
      <Button
        disabled={isBulkTagDisabled}
        startIcon={<LocalOfferOutlinedIcon />}
        onClick={handleClick}
        data-testid="bulk-tag-button"
        size="medium"
      >
        {t('participant.bulkTag.tooltip')}
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClick}
        fullWidth
        disableEscapeKeyDown
        data-testid="bulk-tag-dialog"
      >
        <DialogTitle data-testid="bulk-tag-title">
          <Typography variant="h5" mt={2} mb={1}>
            {t('participant.bulkTag.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <BulkTagsInput
            onChange={handleChange}
            selectedTags={tags}
            hasError={hasError}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClick}
            sx={{ px: 1 }}
            data-testid="bulk-tag-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={addTagsHandler}
            variant="contained"
            disabled={loading}
            data-testid="bulk-tag-add"
          >
            {loading ? <CircularProgress size={20} /> : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default BulkTag
